import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormGroupDirective, NgForm, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IdentityService } from 'src/app/core/services/identity.service';
import { LoginService } from 'src/app/core/services/login.service';
import { TermsAndConditionsDialogComponent } from 'src/app/shared/components/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { runtimeEnv } from 'src/environments/env-loader';
import { NotificationService } from '../../../core/services/notification.service';
import { Idle } from '@ng-idle/core';
import * as CryptoJS from 'crypto-js';
import { OpenRoutes } from 'src/app/core/routes/open-routes';
import { CredentialsService } from 'src/app/core/services/credentials.service';
import { SendDataService } from 'src/app/core/services/send-data.services';
import { StorageService } from 'src/app/core/services/storage.service';
import { NativeBridgeService } from 'src/app/core/services/native-bridge.service';
import { deviceEnvironmentId } from 'src/app/core/enums/device-environment-id';
import { INewLoginResponse } from 'src/app/core/models/response/login.response';
import { CloverService } from 'src/app/core/services/clover.service';
import { isMobileDevice } from '../../../core/utils/navigator.service';
import { ERROR_CODE } from '../../../core/constants/errors.constants';
export class LoginErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}
@Component({
  selector: 'app-public-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  private readonly ENV_KEY: string = runtimeEnv.ENCRYPTKEY;
  private readonly RESEND_TIME: number = runtimeEnv.RESEND2FA;
  private readonly URL = runtimeEnv.URLWEB;

  @ViewChild('inputAccount') inputAccount: ElementRef;
  @ViewChild('inputPassword', { static: true }) inputPassword: ElementRef;
  appEnvironment: string = '';
  loginErrorMatcher = new LoginErrorStateMatcher();
  usuario: any;
  storedUser;
  myForm: UntypedFormGroup;
  loadingFinish: boolean = false;
  temp = false;
  twoAF: boolean = false;
  contador: number = this.RESEND_TIME;
  doubleFactorForm: UntypedFormGroup;
  contadorFormatStr: string = this.fmtMSS(this.contador);
  private hidePasswordTimeout: any;

  public userMessage: string;
  public recoveryLink: string;
  public passwordType: string = 'password';
  public loading: boolean;
  public loginBtnText: string;
  public loginLocked: boolean = false;
  public isClover: boolean = false;
  buttonLogIn: boolean = false;
  showFooter: boolean = false;

  constructor(
    private router: Router,
    private identityService: IdentityService,
    private notificationService: NotificationService,
    private loginService: LoginService,
    private idle: Idle,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private credentials: CredentialsService,
    private storageService: StorageService,
    private sendingDataServices: SendDataService,
    private nativeBridgeService: NativeBridgeService,
    private cloverService: CloverService
  ) {
    this.appEnvironment = this.storageService.get('appEnvironment') || null;
    this.recoveryLink = OpenRoutes.recover;
  }

  ngOnInit() {
    this.blockLogingBtn(false);

    if (this.nativeBridgeService.getEnvironment() == deviceEnvironmentId.CLOVER && isMobileDevice('android')) {
      console.log('is clover');

      this.isClover = true;
    }

    this.myForm = new UntypedFormGroup({
      email: new UntypedFormControl('', [Validators.required, Validators.email]),
      password: new UntypedFormControl('', [Validators.required, Validators.maxLength(50)]),
      remember: new UntypedFormControl('')
    });

    this.doubleFactorForm = new UntypedFormGroup({
      code: new UntypedFormControl('', [Validators.required, Validators.minLength(4), Validators.maxLength(4)])
    });
    this.storedUser = this.identityService.getStoredUser();
  }

  ngAfterViewInit(): void {
    if (this.storedUser) {
      this.myForm.get('email').setValue(this.storedUser);
      this.myForm.get('remember').setValue(true);
      this.inputPassword.nativeElement.focus();
    } else {
      this.inputAccount.nativeElement.focus();
    }
  }

  public get email() {
    return this.myForm.get('email');
  }

  public get password() {
    return this.myForm.get('password');
  }

  isCodeExpired(time: number): boolean {
    let currentTime: number = new Date().getTime();
    return currentTime > time ? true : false;
  }

  public preAuthorizeLogin() {
    const email = this.myForm.controls['email'].value;
    const password = this.myForm.controls['password'].value;
    const remember: boolean = this.myForm.controls['remember'].value;
    this.blockLogingBtn(true);
    let userLogged: INewLoginResponse;

    if (this.isClover) {
      this.loginService
        .preLoginClover(email, password)
        .then(async (result: { object: INewLoginResponse | null; status: number; message: string }) => {
          if (result.status === ERROR_CODE.MFA_SETUP_REQUIRED) {
            this.notificationService.showWarning(result.message);
            this.normalLogin(email, password, remember);
            return;
          }

          if (!result.status) {
            userLogged = result.object;
            this.identityService.setUUID(this.cloverService.getTerminalId());
            this.usuario = userLogged;
            await this.authenticate(true);
            this.blockLogingBtn(false);
          } else {
            this.blockLogingBtn(false);
            this.notificationService.showError(result.message);
            return;
          }
        })
        .catch((err: { message: string }) => {
          if (err && err.message) {
            this.notificationService.showError(err.message);
          }

          this.blockLogingBtn(false);
          return;
        });
    } else {
      this.normalLogin(email, password, remember);
    }
  }

  public normalLogin(email: string, password: string, remember: boolean) {
    this.loginService
      .preAuthorizeLogin(email, password)
      .then((userLogged: INewLoginResponse) => {
        this.loginContinueSteps(userLogged, email, password, remember);
      })
      .catch((error: { status?: string; message?: string }) => {
        if (typeof error === 'undefined' || !error) {
          this.blockLogingBtn(false);
          return;
        }

        switch (error.status) {
          case 'INVALID_FLOW_ID':
            this.blockLogingBtn(false);
            this.notificationService.showError('Usuario o Contraseña incorrectos. Por favor, intenta nuevamente');
            break;
          default:
            this.blockLogingBtn(false);
            if (error.message) {
              this.notificationService.showError(error.message);
            }
            break;
        }
      });
  }

  public loginContinueSteps(userLogged: INewLoginResponse, email: string, password: string, remember: boolean) {
    let user = {
      email,
      password,
      remember,
      response: userLogged
    };
    this.sendingDataServices.setUser(user);

    switch (userLogged.status) {
      case 'OTP_REQUIRED':
        this.blockLogingBtn(false);
        this.router.navigate(['code-verification']);
        break;
      case 'MFA_COMPLETED':
        this.blockLogingBtn(false);
        this.login(userLogged.flow_id, userLogged.cookie);
        break;
      case 'MFA_SETUP_REQUIRED':
        this.blockLogingBtn(false);
        this.router.navigate(['create-code-ga']);
        break;
      default:
        this.blockLogingBtn(false);
        break;
    }
  }

  routerNavigate(url: string) {
    this.router.navigate([url]);
  }

  getItemDecrypted(item) {
    let codeEncrypted = localStorage.getItem(item);
    let codeBytes = CryptoJS.AES.decrypt(codeEncrypted, this.ENV_KEY);
    return JSON.parse(codeBytes.toString(CryptoJS.enc.Utf8));
  }

  isCommerceOrResponsible(usuario): boolean {
    return (usuario.organismo && usuario.organismo.id) || usuario.isResponsible;
  }

  temporizador(seg: number) {
    setTimeout(() => {
      if (seg != 0) {
        this.contador = this.contador - 1;
        this.temporizador(this.contador);
        return (this.contadorFormatStr = this.fmtMSS(this.contador));
      }
    }, 1000);
    if (seg == 0) {
      this.temp = false;
      this.contador = this.RESEND_TIME;
    }
  }

  fmtMSS(s) {
    return (s - (s %= 60)) / 60 + (9 < s ? ':' : ':0') + s;
  }

  navigate(url: string): void {
    window.open(this.URL + url, '_blank');
  }

  async saveEncryptedCode(uc) {
    await this.authenticate();
    let codeStorage = new CodeStorage(
      this.usuario.id.toString(),
      this.doubleFactorForm.get('code').value.toString(),
      uc.fechaExpiracion
    );

    //encrypt code and user id
    let codeEncrypted = CryptoJS.AES.encrypt(JSON.stringify(codeStorage).toString(), this.ENV_KEY).toString();
    localStorage.setItem(`uc${this.usuario.id}`, codeEncrypted);
  }

  async login(flowId: string, cookie: string) {
    const usuario = await this.loginService.authenticate(flowId, cookie);
    this.usuario = usuario;
    this.blockLogingBtn(false);
    await this.authenticate(true);
  }

  async authenticate(code?: boolean) {
    if (this.email.invalid && this.password.invalid) {
      return;
    } else {
      this.blockLogingBtn(true);
    }

    const password: string = this.myForm.controls['password'].value;
    const email: string = this.myForm.controls['email'].value;
    const remember: boolean = this.myForm.controls['remember'].value;

    if (email.length > 0 && password.length > 0) {
      if (!this.loginService.isLoginlocked()) {
        this.loginLocked = false;
        try {
          if (code) {
            this.identityService.saveSession(this.usuario);
          }
          const user = this.identityService.getUser();
          remember ? this.identityService.setStoredUser(email) : this.identityService.removeStoredUser();
          this.validateRolAndRedirect(user);
          this.validateProfileComplete(user);
        } catch (error) {
          this.blockLogingBtn(false);
          this.loginService.addLoginAttempts();
        }
      } else {
        this.loginLocked = true;
        this.notificationService.showWarning(
          this.translate('pages.login.messages.retry'),
          this.translate('pages.login.labels.too_many_tries')
        );
        this.blockLogingBtn(false);
      }
    } else {
      this.blockLogingBtn(false);
    }
  }

  validateRolAndRedirect(user) {
    if (this.identityService.isCommerce() && !user.organismo.rubro) {
      return this.redirectToProfile();
    }
    return this.router.navigate(['/user/cashin-cashout']);
  }

  validateProfileComplete(user) {
    if (
      user.token &&
      !user.statusRegistro.completo &&
      !this.identityService.isCommerce() &&
      !this.identityService.isRolResponsible()
    ) {
      this.router.navigate(['/login']);
      this.identityService.clearSession();
      this.blockLogingBtn(false);
      this.notificationService.showWarning(
        'Su cuenta no se encuentra validada, por favor completa la validación desde la app',
        'Atención:'
      );
    }
  }

  redirectToProfile() {
    this.router.navigate(['/user/my-commerce']);
    this.notificationService.showWarning(
      this.translate('components.layout.complete-profile-msg'),
      this.translate('components.layout.complete-profile-title')
    );
  }

  blockLogingBtn(doBlock: boolean) {
    this.loading = doBlock;
    this.loginBtnText = doBlock ? 'Ingresando...' : 'Ingresar';
  }

  redirectToRegister() {
    this.router.navigate(['/register']);
  }

  togglePassword(inputElement: HTMLInputElement): void {
    this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
    inputElement.type = this.passwordType;

    if (this.passwordType === 'text') {
      this.resetHidePasswordTimeout(inputElement);
    }
  }

  private resetHidePasswordTimeout(inputElement: HTMLInputElement): void {
    if (this.hidePasswordTimeout) {
      clearTimeout(this.hidePasswordTimeout);
    }
    this.hidePasswordTimeout = setTimeout(() => {
      this.passwordType = 'password';
      if (inputElement) {
        inputElement.type = 'password';
      }
    }, 5000);
  }

  get myFormControls(): any {
    return this.myForm.controls;
  }

  translate(key: string, paramKey?: string): string {
    let param;
    if (paramKey) {
      param = { campo: this.translateService.instant(paramKey) };
    }
    return this.translateService.instant(key, param);
  }

  openTyCDialog() {
    this.dialog.open(TermsAndConditionsDialogComponent, {
      data: {
        mustAcceptTermsAndConditions: false
      }
    });
  }
}

class CodeStorage {
  idUsuario: string;
  codigo: string;
  iat: number;

  constructor(idUsuario, codigo, iat) {
    this.idUsuario = idUsuario;
    this.codigo = codigo;
    this.iat = iat;
  }
}
